/**
 * Squeeze & MA/VWAP-Trend cluster — cookbook seed recipes.
 *
 * Each entry validates against the real buildGraphFromRecipe engine.
 * Run the companion test to confirm every recipe builds with zero errors.
 *
 * Node-type and handle references were verified against:
 *   apps/frontend/src/features/backtest/schemas/node-schemas/
 *
 * Key output handles:
 *   bollingerbands: top, mid, bot
 *   keltnerchannel: upper, middle, lower
 *   simplemovingaverage: sma
 *   exponentialmovingaverage: ema
 *   vwap: vwap
 *   condition: true, false
 *   crossover: signal
 *   merge: result
 */

import type { CookbookEntry } from './types'

// ── 2. Squeeze Breakout Entry ─────────────────────────────────
//
// Pattern: squeeze is active AND price breaks above the Keltner upper band.
// The squeeze gate fires while BB is inside KC; the breakout fires when close
// crosses above KC upper. Both merged with AND → Trade Manager.
//
// Nodes: BB + KC → squeeze gate + breakout condition → merge → Trade Manager
const squeezeBreakoutEntry: CookbookEntry = {
  key: 'squeeze-breakout-entry',
  name: 'Squeeze Breakout Entry',
  category: 'squeeze-trend',
  intent: 'Enter long when the squeeze fires and price breaks out above the Keltner upper band.',
  summary:
    'Combines an active BB-inside-KC squeeze with a close-crosses-above-KC-upper breakout to time a long entry at the moment of release.',
  difficulty: 'intermediate',
  whenToUse:
    'Best used on 5-minute or higher timeframes after a clearly visible squeeze (multiple bars of compression). More effective on assets showing positive relative strength during the consolidation phase — strong RS during compression amplifies the breakout move.',
  whatToLookFor:
    'Watch for the squeeze gate to still be active on the bar where close crosses above `kc.upper`. The breakout bar should have an expanding range and ideally increasing volume; a wide-range breakout bar closing near its high on rising volume is the cleanest signal.',
  howItWorks:
    'The recipe rebuilds the full squeeze gate (`bb.top < kc.upper` AND `bb.bot > kc.lower` via two conditions merged with AND), then adds a `crossover(crosses-above)` node checking `price.close` vs `kc.upper`. A second `merge(AND)` requires both `squeeze.result` and `breakout.signal` to fire on the same bar before the Trade Manager places the trade — preventing entries on breakouts that occur without an active compression backdrop.',
  recipe: [
    {
      id: 'price',
      type: 'priceData',
      params: { timeframe: '5m' },
    },
    {
      id: 'bb',
      type: 'bollingerbands',
      params: { period: 20, stdDev: 2.0 },
      inputs: { input_1: 'price.close' },
    },
    {
      id: 'kc',
      type: 'keltnerchannel',
      params: { period: 20, mult: 1.5, atrlen: 10 },
      inputs: { input_1: 'price.ohlcv' },
    },
    {
      id: 'upper_in',
      type: 'condition',
      params: { operator: '<' },
      inputs: { input_1: 'bb.top', input_2: 'kc.upper' },
    },
    {
      id: 'lower_in',
      type: 'condition',
      params: { operator: '>' },
      inputs: { input_1: 'bb.bot', input_2: 'kc.lower' },
    },
    {
      // Squeeze active gate
      id: 'squeeze',
      type: 'merge',
      params: { operator: 'AND' },
      inputs: { input_1: 'upper_in.true', input_2: 'lower_in.true' },
    },
    {
      // Breakout: close crosses above KC upper
      id: 'breakout',
      type: 'crossover',
      params: { crossType: 'crosses-above' },
      inputs: { input_1: 'price.close', input_2: 'kc.upper' },
    },
    {
      // Squeeze AND breakout together
      id: 'gate',
      type: 'merge',
      params: { operator: 'AND' },
      inputs: { input_1: 'squeeze.result', input_2: 'breakout.signal' },
    },
  ],
  tradeManager: {
    params: { orderDirection: 'Long' },
    feeds: { socket: 'trigger', from: 'gate.result' },
  },
  unavailable:
    'The backtest engine reads the Keltner Channel upper and lower lines as its middle line, so this squeeze can never be true. Hidden until the engine is fixed.',
}

// ── 4. MA Pullback Entry ──────────────────────────────────────
//
// Pattern: in an uptrend (close > SMA), enter when price pulls back to the
// SMA and then crosses back above it. The trend filter and the reclaim
// crossover are merged with AND.
//
// Nodes: priceData → simplemovingaverage(20)
//        → condition(close > sma) — trend gate
//        → crossover(crosses-above) — reclaim signal
//        → merge(AND) → Trade Manager
const maPullbackEntry: CookbookEntry = {
  key: 'ma-pullback-entry',
  name: 'MA Pullback Entry',
  category: 'squeeze-trend',
  intent: 'Enter long on a pullback reclaim of the 20-period SMA in an established uptrend.',
  summary:
    'Enters long the bar that price crosses back above the 20 SMA after a pullback, confirmed by an existing uptrend context.',
  difficulty: 'beginner',
  tags: ['Pullback', 'Moving average'],
  whenToUse:
    'Ideal in clearly trending markets where price pulls back to the 20 SMA and then reclaims it on a strong-bodied close. Works well on 5-minute intraday charts during momentum moves. Avoid in choppy, ranging markets where price crosses the moving average repeatedly with no follow-through.',
  whatToLookFor:
    'The `trend_up` condition should be in place before the pullback — meaning price was already above the SMA. The reclaim cross (`reclaim.signal`) should come on a bar with a decisive close, not a doji or spinning top, to confirm buyers are taking control back at the average.',
  howItWorks:
    'A `priceData` node feeds close into `simplemovingaverage(period: 20)`. A `condition(>)` node checks `price.close > sma.sma` for trend context, and a `crossover(crosses-above)` node detects the exact bar `price.close` reclaims the SMA from below. A `merge(AND)` requires both `trend_up.true` and `reclaim.signal` to fire simultaneously — this dual gate prevents false entries on the first touch of the SMA from above and ensures the pullback already validated trend direction.',
  recipe: [
    {
      id: 'price',
      type: 'priceData',
      params: { timeframe: '5m' },
    },
    {
      id: 'sma',
      type: 'simplemovingaverage',
      params: { period: 20 },
      inputs: { input_1: 'price.close' },
    },
    {
      // Trend is up: close must still be above SMA on this bar
      id: 'trend_up',
      type: 'condition',
      params: { operator: '>' },
      inputs: { input_1: 'price.close', input_2: 'sma.sma' },
    },
    {
      // Price reclaims SMA from below — the pullback entry signal
      id: 'reclaim',
      type: 'crossover',
      params: { crossType: 'crosses-above' },
      inputs: { input_1: 'price.close', input_2: 'sma.sma' },
    },
    {
      // Trend context AND reclaim must both fire
      id: 'gate',
      type: 'merge',
      params: { operator: 'AND' },
      inputs: { input_1: 'trend_up.true', input_2: 'reclaim.signal' },
    },
  ],
  tradeManager: {
    params: { orderDirection: 'Long' },
    feeds: { socket: 'trigger', from: 'gate.result' },
  },
}

// ── 5. VWAP Bounce Entry ──────────────────────────────────────
//
// Pattern: enter long when price reclaims VWAP from below (first bounce/reclaim).
// crossover(crosses-above) detects the upward cross of close over vwap.
//
// Nodes: priceData → vwap → crossover(crosses-above) → Trade Manager
const vwapBounceEntry: CookbookEntry = {
  key: 'vwap-bounce-entry',
  name: 'VWAP Bounce Entry',
  category: 'squeeze-trend',
  intent: 'Enter long on the first reclaim of VWAP from below — classic intraday bounce setup.',
  summary:
    'Enters long the bar price crosses back above VWAP, capturing the intraday bounce where institutional orders tend to concentrate.',
  difficulty: 'beginner',
  tags: ['Reclaim', 'VWAP'],
  whenToUse:
    'Best on 5-minute intraday charts during trending sessions. Most effective on the first or second VWAP reclaim of the day — later in the session, VWAP becomes less meaningful as it converges toward price. Works best when the overall tape is bullish (market up, sector strong).',
  whatToLookFor:
    'Look for a clean dip below VWAP followed by a decisive reclaim bar that closes above it. Ideally the reclaim bar has above-average volume and a bullish body, confirming that buyers are stepping in at the volume-weighted fair-value level rather than a random price bounce.',
  howItWorks:
    "A `priceData` node feeds full OHLCV into a `vwap` node (VWAP requires volume data, not just close). A `crossover(crosses-above)` node detects the exact bar `price.close` moves from below to above `vwap.vwap`. The `reclaim.signal` feeds directly into the Trade Manager's Trigger (Long) — no additional filter is included here, so this entry is intentionally designed to be composed with a bias or trend filter in a larger graph.",
  recipe: [
    {
      id: 'price',
      type: 'priceData',
      params: { timeframe: '5m' },
    },
    {
      // VWAP requires full OHLCV (high, low, close, volume)
      id: 'vwap',
      type: 'vwap',
      inputs: { input_1: 'price.ohlcv' },
    },
    {
      // Close crosses back above VWAP — the reclaim/bounce signal
      id: 'reclaim',
      type: 'crossover',
      params: { crossType: 'crosses-above' },
      inputs: { input_1: 'price.close', input_2: 'vwap.vwap' },
    },
  ],
  tradeManager: {
    params: { orderDirection: 'Long' },
    feeds: { socket: 'trigger', from: 'reclaim.signal' },
  },
}

// ── Export ────────────────────────────────────────────────────

export const squeezeTrendSeedRecipes: CookbookEntry[] = [
  squeezeBreakoutEntry,
  maPullbackEntry,
  vwapBounceEntry,
]
